import React, { useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CompanyNameCell } from '../../subscriptions/components/CompanyListTable';
import CompanyTestChip from '../../companies/components/CompanyTestChip';
import WhatsAppCategoryMiniTable from './WhatsAppCategoryMiniTable';
import OriginChip from './OriginChip';
import { formatDate } from '../../../../utils/Utils';

const COLUMN_COUNT = 9;

/**
 * Formata uma data 'Y-m-d H:i:s' (fuso do servidor) usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * WhatsAppCompanyRow - Linha expansível da tabela de empresas: chevron na primeira coluna
 * abre um painel com o detalhamento de envios por tipo de mensagem da empresa no período
 * (mesmo padrão de Collapse de AuditRow/LogRow, com o toggle isolado num IconButton dedicado
 * em vez do clique na linha inteira). (Admin Geral > WhatsApp)
 * @param {Object} props
 * @param {Object} props.company
 * @param {Array<{key: string, label: string}>} props.categories
 * @param {number} props.days - período selecionado, exibido no título do painel
 */
function WhatsAppCompanyRow({ company, categories = [], days }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40, px: 0.5 }}>
          <IconButton
            size="small"
            aria-label={expanded ? 'Recolher detalhamento por tipo' : 'Expandir detalhamento por tipo'}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompanyNameCell name={company.name} companyId={company.company_id} />
            {Number(company.is_test) === 1 && <CompanyTestChip />}
          </Box>
        </TableCell>
        <TableCell>
          <OriginChip origin={company.origin} checkedAt={company.last_status_checked_at} />
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{company.platform_sends ?? 0}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">
            {company.platform_operational ?? 0} / {company.platform_other ?? 0}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{company.own_sends ?? 0}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{company.rate_limited ?? 0}</Typography>
          {(company.policy_blocked || 0) > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              +{company.policy_blocked} política
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{company.limit_alerts ?? 0}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{fmtDate(company.last_send_at)}</Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} sx={{ py: 0, border: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#F8F9FC' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Por tipo de mensagem (últimos {days} dias)
              </Typography>
              <WhatsAppCategoryMiniTable
                categories={categories}
                byCategory={company.by_category || {}}
                emptyMessage="Sem envios no período."
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default WhatsAppCompanyRow;
